import { Emitter } from 'mitt';
import { useContext, useEffect, useState, useMemo } from 'haunted/lib/core';

import { createContext } from '../../utils';

type SliderEvent = {
  /**
   * Move slider to next slide
   */
  nextSlide: undefined;
  /**
   * Special event that is used from the timer to move the slider without
   * changing its playing state from `auto` to `paused`.
   */
  autoNextSlide: undefined;
  /**
   * Move slider to previous slide
   */
  prevSlide: undefined;
  /**
   * Move slider to slide with index
   */
  moveToSlide: { slide: number };
  /**
   * Event triggered by keenSlider when drag is detected
   */
  keenDragStart: undefined;
  /**
   * Event triggered by keenSlider when slide is changed
   */
  keenSlideChanged: { currentSlide: number };
};

type SliderContextValue = {
  initialSlide: number;
  numberOfSlides: number;
  state: 'paused' | 'play';
  emitterRef: { current: Emitter<SliderEvent> };
};

const SliderContext = createContext<SliderContextValue | undefined>(undefined);

/* -----------------------------------------------------------------------------
 * useSlider
 * ---------------------------------------------------------------------------*/

function useSlider(): SliderContextValue | {} {
  return useContext(SliderContext) || {};
}

/* -----------------------------------------------------------------------------
 * useCurrentSlide
 * ---------------------------------------------------------------------------*/

function useCurrentSlide() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const context = useContext(SliderContext);

  useEffect(() => {
    if (!context) {
      return;
    }

    function onSlideChangedHandler(props: SliderEvent['keenSlideChanged']) {
      setCurrentSlide(props.currentSlide);
    }

    context.emitterRef.current.on('keenSlideChanged', onSlideChangedHandler);

    return () => {
      context.emitterRef.current.off('keenSlideChanged', onSlideChangedHandler);
    };
  }, [context]);

  return { currentSlide, totalSlides: context?.numberOfSlides ?? 0 };
}

/* -----------------------------------------------------------------------------
 * useSliderPagination
 * ---------------------------------------------------------------------------*/

type SliderPagination = {
  nextSlide(): void;
  prevSlide(): void;
  moveToSlide(slide: number): void;
};

const initialSliderPagination: SliderPagination = {
  nextSlide() {},
  prevSlide() {},
  moveToSlide() {},
};

function useSliderPagination(): SliderPagination {
  const context = useContext(SliderContext);

  return useMemo(() => {
    if (!context) {
      return initialSliderPagination;
    }

    return {
      nextSlide() {},
      prevSlide() {},
      moveToSlide(slide: number) {
        context.emitterRef.current.emit('moveToSlide', { slide });
      },
    };
  }, [context]);
}

const SliderContextProvider = SliderContext.Provider;

export type { SliderEvent, SliderContextValue };
export {
  SliderContextProvider,
  useSlider,
  useCurrentSlide,
  useSliderPagination,
};
